import React, { createContext, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';
import { jwtDecode } from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project-imports
import Loader from 'components/Loader';
import axios from 'utils/axios';
import { getMessaging } from 'firebase/messaging';
import { getFirebaseWebApp } from 'config/firebaseWeb';
import {
  clearWebFcmTokenOnClient,
  isWebPushSupported,
  obtainWebFcmToken,
  subscribeForegroundPush,
  showForegroundNativeNotification
} from 'utils/webPush';
import { loginService, logoutService, getUserByToken, forgotPasswordService, registerWebPushToken } from 'services/auth.service';

// types
import { AuthProps, JWTContextType } from 'types/auth';
import { KeyedObject } from 'types/root';

const chance = new Chance();

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  token: null
};

const verifyToken: (st: string) => boolean = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  const decoded: KeyedObject = jwtDecode(serviceToken);
  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken?: string | null) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    axios.defaults.headers.common.authToken = `${serviceToken}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axios.defaults.headers.common.authToken;
  }
};

async function setupAssessorWebPush(): Promise<void> {
  if (!isWebPushSupported()) {
    return;
  }
  try {
    const token = await obtainWebFcmToken();

    if (!token) {
      return;
    }
    const messaging = getMessaging(getFirebaseWebApp());
   
    subscribeForegroundPush(messaging, (title, body) => {
      showForegroundNativeNotification(title, body);
    });
    await registerWebPushToken({ webFcmToken: token });
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug('[webPush] token registered');
    }
  } catch (e) {
    console.error('[webPush] setup failed', e);
  }
}

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem('serviceToken');
        if (serviceToken && verifyToken(serviceToken)) {
          setSession(serviceToken);
          const response = await getUserByToken();
          const user = response.data?.data ?? response.data?.user;
          if (user) {
            dispatch({
              type: LOGIN,
              payload: {
                isLoggedIn: true,
                isInitialized: true,
                user,
                token: serviceToken
              }
            });
            void setupAssessorWebPush();
          } else {
            setSession(null);
            dispatch({ type: LOGOUT });
          }
        } else {
          setSession(null);
          dispatch({ type: LOGOUT });
        }
      } catch (err) {
        console.error(err);
        setSession(null);
        dispatch({ type: LOGOUT });
      }
    };

    init();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const loginDetails = { email, password };
      const response = await loginService(loginDetails);
      const responseData = response.data?.data ?? response.data;
      const token = responseData?.token ?? responseData?.loginToken;
      const { token: _t, ...user } = responseData || {};
      if (token) setSession(token);
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: (user && Object.keys(user).length ? user : responseData) ?? responseData,
          token
        }
      });
      void setupAssessorWebPush();
    } catch (error: unknown) {
      const err = error as { response?: { data?: unknown } };
      throw err?.response?.data || error;
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers!),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = async () => {
    try {
      const logoutReq = { userId: state?.user?._id };
      if (state?.user?._id) {
        await logoutService(logoutReq);
      }
    } catch (err) {
      console.error(err);
    } finally {
      try {
        await clearWebFcmTokenOnClient();
      } catch {
        /* ignore */
      }
      setSession(null);
      dispatch({ type: LOGOUT });
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await forgotPasswordService({ email });
    } catch (error: unknown) {
      const err = error as { response?: { data?: unknown } };
      throw err?.response?.data || error;
    }
  };

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return <JWTContext.Provider value={{ ...state, login, logout, register, resetPassword, updateProfile }}>{children}</JWTContext.Provider>;
};

export default JWTContext;
